import type { QuoteBody } from "~/shared/schemas/quote-schema.ts";
import type { QuoteRecall } from "~/shared/schemas/shipment-management-schema.ts";

type QuoteBodyLike = Partial<QuoteBody> & {
	calculatedLinearFeet?: number | null;
};

const mapLocation = (location: QuoteRecall["origin"]): QuoteBody["origin"] => ({
	postalCode: location.postalCode ?? "",
	city: location.city ?? "",
	state: location.state ?? "",
	country: location.country ?? "",
});

/**
 * Maps a shipment's `quoteRecall` payload into a `QuoteBody`-shaped object so it
 * can be fed through {@link mapQuoteBodyToQuoteState} to populate the create
 * quote form. Used by the "Copy Quote" action, which starts a fresh quote from
 * an existing shipment without re-fetching the original quote by id.
 */
export const mapQuoteRecallToQuoteBody = (
	recall: QuoteRecall,
): QuoteBodyLike => ({
	origin: mapLocation(recall.origin),
	destination: mapLocation(recall.destination),
	accessorials: recall.accessorials.map((accessorial) => ({
		id: accessorial.id,
		description: accessorial.name ?? "",
	})),
	quoteCommodities: recall.quoteCommodities,
	pickupDetails: recall.pickupDetails,
	deliveryDetails: recall.deliveryDetails,
	shipmentDate: recall.shipmentDate ?? undefined,
	customer: { id: recall.customerId ?? 0, name: "" },
	customerId: recall.customerId ?? undefined,
	specialInstructions: recall.specialInstructions,
	totalLinearFeet:
		recall.totalLinearFeet == null ? undefined : String(recall.totalLinearFeet),
	calculatedLinearFeet: recall.calculatedLinearFeet,
	pieceCaseCount: recall.pieceCaseCount,
	publicGuid: recall.publicGuid,
	templateId: recall.templateId,
	sourceId: recall.sourceId,
});
